export function startOfLocalDay(d: Date): Date {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
}

/** Local calendar `YYYY-MM-DD` (do not use `toISOString().split('T')[0]` for calendar dates — it is UTC). */
export function formatLocalDateYmd(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

/** Parse API / Gantt values as a local calendar day (`YYYY-MM-DD` is not parsed as UTC midnight here). */
export function parseLocalCalendarDate(value: Date | string | number): Date {
    if (value instanceof Date) {
        return startOfLocalDay(value);
    }
    const s = String(value).trim();
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    if (m) {
        return startOfLocalDay(new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
    }
    return startOfLocalDay(new Date(s));
}

export function isSameCalendarDay(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function isSameCalendarMonth(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}

/** True when `day` falls in [rangeStart, rangeStart + spanMonths) in the local calendar. */
function localMonthRangeContains(rangeStart: Date, spanMonths: number, day: Date): boolean {
    const t0 = startOfLocalDay(new Date(rangeStart.getFullYear(), rangeStart.getMonth(), 1));
    const t1 = new Date(t0);
    t1.setMonth(t1.getMonth() + spanMonths);
    const tt = startOfLocalDay(day).getTime();
    return tt >= t0.getTime() && tt < t1.getTime();
}

/** ISO week: Monday 00:00 local. */
export function startOfIsoWeekMonday(d: Date): Date {
    const x = startOfLocalDay(d);
    const dow = x.getDay();
    const daysFromMonday = (dow + 6) % 7;
    x.setDate(x.getDate() - daysFromMonday);
    return x;
}

export function isDateInSameLocalWeekAs(anchor: Date, date: Date): boolean {
    const weekStart = startOfIsoWeekMonday(anchor).getTime();
    const day = startOfLocalDay(date).getTime();
    const weekEnd = weekStart + 7 * 86_400_000;
    return day >= weekStart && day < weekEnd;
}

/**
 * How a timeline or scale cell maps to the calendar.
 * - `day`: one column = one day (weekend / current week / today use the anchor date’s weekday).
 * - `week`: one column = one week (no weekend tint from the anchor weekday alone).
 * - `month`: one column = one calendar month (or `spanMonths` consecutive months).
 */
export type GanttTimelineCellStep = 'day' | 'week' | 'month';

export interface GanttTimelineCellClassOptions {
    today?: Date;
    cellStep?: GanttTimelineCellStep;
    /** Only for `cellStep: 'month'`. When &gt; 1, the cell spans multiple months (e.g. quarter header). */
    spanMonths?: number;
}

export function buildGanttTimelineCellClasses(date: Date, today: Date = new Date(), options?: GanttTimelineCellClassOptions): string {
    const t = options?.today ?? today;
    const cellStep = options?.cellStep ?? 'day';

    if (cellStep === 'month') {
        const span = options?.spanMonths ?? 1;
        const parts: string[] = [];
        if (span > 1) {
            if (localMonthRangeContains(date, span, t)) {
                parts.push('gantt_today');
            }
        } else if (isSameCalendarMonth(date, t)) {
            parts.push('gantt_today');
        }
        return parts.join(' ');
    }

    if (cellStep === 'week') {
        const parts: string[] = [];
        if (isDateInSameLocalWeekAs(t, date)) {
            parts.push('gantt_current_week');
        }
        return parts.join(' ');
    }

    const parts: string[] = [];
    const dow = date.getDay();
    if (dow === 0 || dow === 6) {
        parts.push('weekend');
    }
    if (isDateInSameLocalWeekAs(t, date)) {
        parts.push('gantt_current_week');
    }
    if (isSameCalendarDay(date, t)) {
        parts.push('gantt_today');
    }
    return parts.join(' ');
}
